import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, HardDrive, Users, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetStorageSourcesQuery,
  useGetStorageProvidersQuery,
  useCreateStorageSourceMutation,
  useUpdateStorageSourceMutation,
  useDeleteStorageSourceMutation,
  useTestStorageSourceMutation,
} from '@/store/api/apiSlice';
import type { StorageSource, StorageProviderDescriptor, StorageProxyType } from '@/types';
import { formatBytes } from '@/lib/storage-transfer';
import {
  buildStorageSourcePayload,
  DEFAULT_DOCKER_IMAGE,
  defaultFormData,
  type SourceFormData,
} from '@/lib/storage-source-payload';
import { useToast } from '@/components/shared/Toast';
import { wsClient } from '@/lib/ws';
import { StorageEventType, STORAGE_UPDATES_ROOM } from '@cluster/types';

/**
 * Render a `record`-typed config value (tags/headers/readHeaders) into the
 * textarea. Objects are pretty-printed; raw strings are shown as typed.
 */
function recordFieldToText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return JSON.stringify(value, null, 2);
  return '';
}

/**
 * Parse textarea input for a `record`-typed field. Valid JSON is stored as an
 * object; invalid input keeps the raw string (buildStorageSourcePayload omits
 * it so the backend schema is never fed a plain string).
 */
function parseRecordFieldText(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return ''; // empty → pruned from the payload
  try {
    return JSON.parse(trimmed);
  } catch {
    return text; // raw string; omitted by buildStorageSourcePayload
  }
}

/**
 * Compute the "Usage" column percentage (0–100+ clamped at render time by
 * `Math.min(pct, 100)`). Guards against division-by-zero: when `capacityBytes`
 * is missing, zero, or negative the result is `0`, never `NaN`/`Infinity`.
 * Non-finite `usedBytes` also yields `0`. Exported as a pure function so the
 * cell logic can be unit-tested directly.
 */
export function computeUsagePercent(
  usedBytes: number | undefined,
  capacityBytes: number | undefined,
): number {
  const used = usedBytes ?? 0;
  const cap = capacityBytes ?? 0;
  return Number.isFinite(used) && cap > 0 ? Math.round((used / cap) * 100) : 0;
}

/** Names of the selected provider's record-typed config fields. */
function recordFieldNamesOf(provider: StorageProviderDescriptor | undefined): string[] {
  return (provider?.formFields ?? [])
    .filter((f) => f.type === 'record')
    .map((f) => f.name);
}

export function StorageSourcesPage() {
  const navigate = useNavigate();
  const { error: toastError } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<StorageSource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StorageSource | null>(null);
  const [formData, setFormData] = useState<SourceFormData>(defaultFormData);
  const [testResult, setTestResult] = useState<{ status: string; message?: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const { data: sources, isLoading, refetch: refetchSources } = useGetStorageSourcesQuery();
  const { data: providers } = useGetStorageProvidersQuery();
  const [createSource, { isLoading: isCreating }] = useCreateStorageSourceMutation();
  const [updateSource, { isLoading: isUpdating }] = useUpdateStorageSourceMutation();
  const [deleteSource, { isLoading: isDeleting }] = useDeleteStorageSourceMutation();
  const [testSource] = useTestStorageSourceMutation();

  // Use ref to access refetch without causing the WS-effect to re-run
  const refetchSourcesRef = useRef(refetchSources);
  useEffect(() => {
    refetchSourcesRef.current = refetchSources;
  }, [refetchSources]);

  // WebSocket real-time refresh (no polling)
  useEffect(() => {
    const onSourceEvent = () => { refetchSourcesRef.current(); };

    const unsubConnected = wsClient.on('connected', () => {
      wsClient.subscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    });

    if (!wsClient.connected) {
      wsClient.connect().catch(() => {});
    } else {
      wsClient.subscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    }

    const unsubs = [
      unsubConnected,
      wsClient.on(StorageEventType.UPLOAD_COMPLETED, onSourceEvent),
      wsClient.on(StorageEventType.UPLOAD_FAILED, onSourceEvent),
      wsClient.on(StorageEventType.FILE_DELETED, onSourceEvent),
      wsClient.on(StorageEventType.REPLICATION_STATUS, onSourceEvent),
    ];

    return () => {
      unsubs.forEach((u) => u());
      wsClient.unsubscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    };
  }, []);

  const sourceList = (sources as StorageSource[] | undefined) ?? [];
  const providerList = (providers as StorageProviderDescriptor[] | undefined) ?? [];
  const selectedProvider = providerList.find((p) => p.type === formData.type);

  const columns: ColumnDef<StorageSource, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => <StatusBadge status={row.original.type} />,
    },
    {
      accessorKey: 'supportsByteRange',
      header: 'Features',
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.supportsByteRange && (
            <Badge variant="outline" className="text-xs">Byte Range</Badge>
          )}
          {row.original.chunkConfig?.enabled && (
            <Badge variant="outline" className="text-xs">
              Chunks {formatBytes((row.original.chunkConfig.chunkSizeMB ?? 0) * 1024 * 1024)}
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'accountsCount',
      header: 'Accounts',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Users className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">{row.original.accountsCount ?? 0}</span>
        </div>
      ),
    },
    {
      accessorKey: 'capacityBytes',
      header: 'Usage',
      cell: ({ row }) => {
        const used = row.original.usedBytes ?? 0;
        const cap = row.original.capacityBytes ?? 0;
        const pct = computeUsagePercent(used, cap);
        return (
          <div className="flex items-center gap-2 min-w-[120px]">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatBytes(used)} / {formatBytes(cap)}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge status={row.original.isActive ? 'active' : 'stopped'} />
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditingSource(null);
    setFormData({ ...defaultFormData });
    setTestResult(null);
    setModalOpen(true);
  }

  function openEdit(source: StorageSource) {
    const proxy = (source.proxy ?? {}) as Record<string, unknown>;
    const proxyType =
      proxy.type === 'http' || proxy.type === 'openvpn' || proxy.type === 'wireguard'
        ? proxy.type
        : 'none';
    const { type: _proxyType, ...proxyRest } = proxy;
    const { type: _configType, ...configRest } = (source.config ?? {}) as Record<string, unknown>;

    setEditingSource(source);
    setFormData({
      name: source.name,
      type: source.type,
      dockerImage: source.dockerImage ?? DEFAULT_DOCKER_IMAGE,
      supportsByteRange: source.supportsByteRange,
      strategy: source.strategy ?? 'least_used',
      chunkEnabled: source.chunkConfig?.enabled ?? false,
      chunkSizeMB: source.chunkConfig?.chunkSizeMB ?? 5,
      proxyType,
      proxyConfig: { ...proxyRest },
      readEndpoint: source.readEndpoint ?? '',
      config: { ...configRest },
    });
    setTestResult(null);
    setModalOpen(true);
  }

  function handleProviderFieldChange(key: string, value: unknown) {
    setFormData((prev) => ({
      ...prev,
      config: { ...prev.config, [key]: value },
    }));
  }

  function handleProxyFieldChange(key: string, value: unknown) {
    setFormData((prev) => ({
      ...prev,
      proxyConfig: { ...prev.proxyConfig, [key]: value },
    }));
  }

  async function handleSave() {
    // Client-side required-field validation (name + selected provider required fields)
    const missing: string[] = [];
    if (!formData.name.trim()) missing.push('name');
    (selectedProvider?.formFields ?? [])
      .filter((f) => f.required)
      .forEach((f) => {
        const value = formData.config[f.name];
        if (value === undefined || value === null || value === '') missing.push(f.name);
      });
    if (missing.length > 0) {
      toastError(`Faltan campos obligatorios: ${missing.join(', ')}`);
      return;
    }

    const payload = buildStorageSourcePayload(formData, recordFieldNamesOf(selectedProvider));

    try {
      if (editingSource) {
        await updateSource({ id: editingSource._id, body: payload }).unwrap();
      } else {
        await createSource(payload).unwrap();
      }
      setModalOpen(false);
    } catch (err) {
      const data = (err as {
        data?: {
          error?: { message?: string; details?: Array<{ field?: string; message?: string }> };
          message?: string;
          details?: Array<{ field?: string; message?: string }>;
        };
      })?.data;
      const details = data?.error?.details ?? data?.details;
      let msg =
        data?.error?.message ??
        data?.message ??
        'No se pudo guardar la fuente de almacenamiento. Inténtalo de nuevo.';
      if (Array.isArray(details) && details.length > 0) {
        msg = details
          .map((d) => (d.field ? `${d.field}: ${d.message ?? ''}` : d.message))
          .filter(Boolean)
          .join(', ');
      }
      toastError(msg);
    }
  }

  async function handleDelete() {
    if (deleteTarget) {
      await deleteSource(deleteTarget._id).unwrap();
      setDeleteTarget(null);
    }
  }

  async function handleTestConnection() {
    if (!editingSource) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const { jobId } = await testSource(editingSource._id).unwrap();
      // Mark as initiated — the backend will process the test asynchronously
      setTestResult({ status: 'completed', message: `Test job ${jobId} initiated` });
      setIsTesting(false);
    } catch {
      setTestResult({ status: 'failed', message: 'Failed to start test' });
      setIsTesting(false);
    }
  }

  function renderProviderFields() {
    if (!selectedProvider) return null;
    return (
      <div className="space-y-3 rounded-lg border p-3">
        <p className="text-sm font-medium">Provider Configuration</p>
        {selectedProvider.formFields.map((field) => (
          <div key={field.name} className="space-y-1">
            <Label htmlFor={`provider-${field.name}`} className="text-xs">
              {field.label}
              {field.required && <span className="text-destructive"> *</span>}
            </Label>
            {field.type === 'boolean' ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  id={`provider-${field.name}`}
                  checked={Boolean(formData.config[field.name] ?? field.defaultValue ?? false)}
                  onChange={(e) => handleProviderFieldChange(field.name, e.target.checked)}
                />
                {field.defaultValue === true ? 'Enabled by default' : ''}
              </label>
            ) : field.type === 'select' ? (
              <select
                id={`provider-${field.name}`}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                value={String(formData.config[field.name] ?? field.defaultValue ?? '')}
                onChange={(e) => handleProviderFieldChange(field.name, e.target.value)}
              >
                <option value="">Select...</option>
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : field.type === 'number' ? (
              <Input
                id={`provider-${field.name}`}
                type="number"
                value={String(formData.config[field.name] ?? field.defaultValue ?? '')}
                onChange={(e) => handleProviderFieldChange(field.name, Number(e.target.value))}
              />
            ) : field.type === 'record' ? (
              <textarea
                id={`provider-${field.name}`}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                value={recordFieldToText(formData.config[field.name] ?? field.defaultValue)}
                onChange={(e) =>
                  handleProviderFieldChange(field.name, parseRecordFieldText(e.target.value))
                }
                placeholder='{"key": "value"}'
              />
            ) : (
              <Input
                id={`provider-${field.name}`}
                type={field.type === 'password' ? 'password' : field.type === 'url' ? 'url' : 'text'}
                value={String(formData.config[field.name] ?? field.defaultValue ?? '')}
                onChange={(e) => handleProviderFieldChange(field.name, e.target.value)}
              />
            )}
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Storage Sources</h1>
          <p className="text-sm text-muted-foreground">
            Manage storage providers and their accounts
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Source
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <p className="text-sm text-muted-foreground">
            Click a source row to manage its accounts
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : sourceList.length === 0 ? (
            <EmptyState
              title="No storage sources"
              description="Add a storage source to get started"
              action={<Button onClick={openCreate}>Add Source</Button>}
            />
          ) : (
            <DataTable
              columns={columns}
              data={sourceList}
              onRowClick={(row) => navigate(`/storage/sources/${row._id}/accounts`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSource ? 'Edit Source' : 'Add Source'}</DialogTitle>
            <DialogDescription>
              {editingSource
                ? 'Update storage source configuration.'
                : 'Configure a new storage source.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="my-s3-source"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Provider Type</Label>
              <select
                id="type"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value, config: {} })}
              >
                <option value="">Select provider...</option>
                {providerList.map((p) => (
                  <option key={p.type} value={p.type}>{p.label}</option>
                ))}
              </select>
            </div>

            {/* Dynamic provider fields */}
            {renderProviderFields()}

            {/* Chunk configuration */}
            <div className="space-y-3 rounded-lg border p-3">
              <p className="text-sm font-medium">Chunk Configuration</p>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="byteRange"
                  checked={formData.supportsByteRange}
                  onChange={(e) => setFormData({ ...formData, supportsByteRange: e.target.checked })}
                  disabled={selectedProvider?.capabilities.supportsByteRange === false}
                />
                <Label htmlFor="byteRange" className="text-xs">
                  Enable byte-range requests
                </Label>
              </div>
              {selectedProvider?.capabilities.supportsByteRange === false && (
                <p className="text-xs text-muted-foreground">
                  This provider does not support byte-range requests.
                </p>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="chunkEnabled"
                  checked={formData.chunkEnabled}
                  onChange={(e) => setFormData({ ...formData, chunkEnabled: e.target.checked })}
                />
                <Label htmlFor="chunkEnabled" className="text-xs">Enable chunking</Label>
              </div>
              <div className="space-y-1">
                <Label htmlFor="chunkSize" className="text-xs">
                  Chunk size (MB)
                </Label>
                <Input
                  id="chunkSize"
                  type="number"
                  min={5}
                  max={25}
                  value={formData.chunkSizeMB}
                  onChange={(e) =>
                    setFormData({ ...formData, chunkSizeMB: Number(e.target.value) })
                  }
                />
              </div>
            </div>

            {/* Proxy section */}
            <div className="space-y-3 rounded-lg border p-3">
              <p className="text-sm font-medium">Proxy</p>
              <div className="space-y-1">
                <Label htmlFor="proxyType" className="text-xs">Type</Label>
                <select
                  id="proxyType"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={formData.proxyType}
                  onChange={(e) => setFormData({ ...formData, proxyType: e.target.value as StorageProxyType, proxyConfig: {} })}
                >
                  <option value="none">None</option>
                  <option value="http">HTTP</option>
                  <option value="openvpn">OpenVPN</option>
                  <option value="wireguard">WireGuard</option>
                </select>
              </div>
              {formData.proxyType === 'http' && (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="proxyUrl" className="text-xs">Proxy URL</Label>
                    <Input
                      id="proxyUrl"
                      placeholder="http://proxy:8080"
                      value={String(formData.proxyConfig.url ?? '')}
                      onChange={(e) => handleProxyFieldChange('url', e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label htmlFor="proxyUsername" className="text-xs">Username (optional)</Label>
                      <Input
                        id="proxyUsername"
                        value={String(formData.proxyConfig.username ?? '')}
                        onChange={(e) => handleProxyFieldChange('username', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="proxyPassword" className="text-xs">Password (optional)</Label>
                      <Input
                        id="proxyPassword"
                        type="password"
                        value={String(formData.proxyConfig.password ?? '')}
                        onChange={(e) => handleProxyFieldChange('password', e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
              {(formData.proxyType === 'openvpn' || formData.proxyType === 'wireguard') && (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="proxyInterface" className="text-xs">Interface</Label>
                    <Input
                      id="proxyInterface"
                      placeholder={formData.proxyType === 'openvpn' ? 'tun0' : 'wg0'}
                      value={String(formData.proxyConfig.interface ?? '')}
                      onChange={(e) => handleProxyFieldChange('interface', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="proxyConfigPath" className="text-xs">Config path (optional)</Label>
                    <Input
                      id="proxyConfigPath"
                      placeholder="/etc/openvpn/client.conf"
                      value={String(formData.proxyConfig.configPath ?? '')}
                      onChange={(e) => handleProxyFieldChange('configPath', e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="readEndpoint">Read Endpoint (optional)</Label>
              <Input
                id="readEndpoint"
                placeholder="https://cdn.example.com"
                value={formData.readEndpoint}
                onChange={(e) => setFormData({ ...formData, readEndpoint: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dockerImage">Docker Image</Label>
              <Input
                id="dockerImage"
                placeholder="ghcr.io/cluster/storage-adapter:latest"
                value={formData.dockerImage}
                onChange={(e) => setFormData({ ...formData, dockerImage: e.target.value })}
              />
            </div>

            {/* Test connection */}
            {editingSource && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestConnection}
                  disabled={isTesting}
                >
                  <Zap className="mr-1 h-3 w-3" />
                  {isTesting ? 'Testing...' : 'Test Connection'}
                </Button>
                {testResult && (
                  <StatusBadge status={testResult.status === 'completed' ? 'available' : 'failed'} />
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating || isUpdating}>
              {(isCreating || isUpdating) ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Storage Source"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will remove all associated accounts. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
